// BM-H81 P2 - CapacityConversationPanel.
//
// Structured conversation panel wired to the LPA engine's V-CAP01..06
// hard stops and advisories. Renders the MCA 2005 s.2 and s.3
// checklist as six questions; each answer is stored under
// ``instrument.capacity_review`` on the answers dict so the engine
// can consume it on the next dry run.

import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { LpaIntro, LpaQuestion } from "./lpa/LpaQuestion";

// BM-H3669 - each row carries its own tooltip (lpa.capacity.<id>) and the panel
// opens with an intro. The row wording, test ids and values written are
// unchanged.

const QUESTIONS = [
  {
    id: "understands_information",
    label: "The donor can understand the information relevant to making an LPA (MCA 2005 s.3(1)(a)).",
    testid: "capacity-q-understands-information",
  },
  {
    id: "retains_information",
    label: "The donor can retain the information long enough to make the decision (MCA 2005 s.3(1)(b)).",
    testid: "capacity-q-retains-information",
  },
  {
    id: "weighs_information",
    label: "The donor can use or weigh the information as part of the decision-making process (MCA 2005 s.3(1)(c)).",
    testid: "capacity-q-weighs-information",
  },
  {
    id: "communicates_decision",
    label: "The donor can communicate the decision (MCA 2005 s.3(1)(d)).",
    testid: "capacity-q-communicates-decision",
  },
  {
    id: "free_from_pressure",
    label: "The donor is making this decision free from undue pressure (MCA CoP 7.55 to 7.62).",
    testid: "capacity-q-free-from-pressure",
  },
  {
    id: "notes",
    label: "Any additional notes for the certificate provider.",
    testid: "capacity-q-notes",
    freeText: true,
  },
];

// eslint-disable-next-line react/prop-types
export default function CapacityConversationPanel({ value, onChange }) {
  const state = value || {};

  function update(id, val) {
    onChange?.({ ...state, [id]: val });
  }

  const allYes = QUESTIONS
    .filter((q) => !q.freeText)
    .every((q) => state[q.id] === "yes");

  return (
    <section
      data-testid="capacity-conversation-panel"
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        padding: 16,
        background: "#fff",
      }}
    >
      <h2 style={{ fontSize: 16, marginTop: 0, marginBottom: 8, color: "#0f172a" }}>
        Capacity conversation checklist
      </h2>
      <LpaIntro id="capacity_panel" />

      {QUESTIONS.map((q) => (
        <LpaQuestion
          key={q.id}
          qid={`lpa.capacity.${q.id}`}
          testid={q.testid}
          label={q.label}
          labelStyle={{ fontWeight: 400, marginBottom: 8, color: "#0f172a" }}
          style={{
            padding: 12,
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            marginBottom: 10,
            background: "#f8fafc",
          }}
        >
          {q.freeText ? (
            <textarea
              data-testid={`${q.testid}-textarea`}
              value={state[q.id] || ""}
              onChange={(e) => update(q.id, e.target.value)}
              rows={3}
              style={{
                width: "100%",
                padding: 8,
                border: "1px solid #cbd5e1",
                borderRadius: 6,
                fontFamily: "inherit",
                fontSize: 13,
                resize: "vertical",
              }}
            />
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              {["yes", "no", "unsure"].map((opt) => {
                const active = state[q.id] === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    data-testid={`${q.testid}-${opt}`}
                    onClick={() => update(q.id, opt)}
                    style={{
                      padding: "4px 12px",
                      borderRadius: 999,
                      border: "1px solid",
                      borderColor: active ? "#4338ca" : "#cbd5e1",
                      background: active ? "#4338ca" : "#fff",
                      color: active ? "#fff" : "#334155",
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 600,
                      textTransform: "capitalize",
                    }}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          )}
        </LpaQuestion>
      ))}

      {allYes ? (
        <div
          data-testid="capacity-panel-status-clear"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: 10,
            marginTop: 6,
            background: "#f0fdf4",
            border: "1px solid #86efac",
            borderRadius: 8,
            color: "#166534",
            fontSize: 13,
          }}
        >
          <CheckCircle2 size={16} />
          All capacity limbs affirmed. The certificate provider can
          proceed to LP1H/LP1F section 10.
        </div>
      ) : (
        <div
          data-testid="capacity-panel-status-outstanding"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: 10,
            marginTop: 6,
            background: "#fffbeb",
            border: "1px solid #fde68a",
            borderRadius: 8,
            color: "#b45309",
            fontSize: 13,
          }}
        >
          <AlertTriangle size={16} />
          One or more capacity limbs are not yet affirmed. Do not sign
          the certificate until the donor's response to each is
          recorded.
        </div>
      )}
    </section>
  );
}
